import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Skeleton } from '@/components/ui/skeleton';
import { apiClient } from '@/lib/api-client';

/** The PDF viewer's open parameters: page width, no side panel. */
export const VIEWER_PARAMS = '#view=FitH&navpanes=0';

/**
 * The expense's PDF, read through finance (`GET /v1/expenses/{id}/document`, no signed URLs,
 * design §2.4) as a Blob and shown from an object URL. The URL is revoked when the preview goes
 * away or the document changes, so the bytes do not stay in memory.
 *
 * `revision` changes when the document is replaced, so the preview reads it again.
 */
export function DocumentPreview({
  expenseId,
  revision,
}: {
  readonly expenseId: string;
  readonly revision: string;
}) {
  const { t } = useTranslation('expenses');
  const [state, setState] = useState<
    { status: 'loading' } | { status: 'error' } | { status: 'ready'; url: string }
  >({ status: 'loading' });

  useEffect(() => {
    let active = true;
    let url: string | null = null;
    setState({ status: 'loading' });
    apiClient
      .GET('/v1/expenses/{expenseId}/document', {
        params: { path: { expenseId } },
        parseAs: 'blob',
      })
      .then(({ data, error, response }) => {
        if (!active) return;
        if (error !== undefined || !response.ok || !(data instanceof Blob)) {
          setState({ status: 'error' });
          return;
        }
        url = URL.createObjectURL(data);
        setState({ status: 'ready', url });
      })
      .catch(() => {
        if (active) setState({ status: 'error' });
      });
    return () => {
      active = false;
      if (url !== null) URL.revokeObjectURL(url);
    };
  }, [expenseId, revision]);

  if (state.status === 'loading') {
    return <Skeleton aria-label={t('detail.document.loading')} className="h-[520px] w-full" />;
  }
  if (state.status === 'error') {
    return (
      <p role="alert" className="text-destructive text-sm">
        {t('detail.document.loadError')}
      </p>
    );
  }
  return (
    <>
      {/* Fit width, no thumbnail sidebar: the whole receipt shows in the pane (M14). */}
      <iframe
        src={`${state.url}${VIEWER_PARAMS}`}
        title={t('detail.document.previewTitle')}
        className="border-border h-[520px] w-full rounded-lg border bg-white max-sm:hidden"
      />
      {/* A phone's viewer is a dark strip with a fragment of text: the browser opens it instead. */}
      <a
        href={state.url}
        target="_blank"
        rel="noreferrer"
        className="border-border bg-card hover:bg-muted inline-flex min-h-11 w-full items-center justify-center rounded-lg border px-4 text-sm font-medium sm:hidden"
      >
        {t('detail.document.open')}
      </a>
    </>
  );
}
